/**
 * Admin panel — password-protected control panel.
 *
 * Tabs: Dashboard | Games | Settings
 * Auth: single shared ADMIN_SECRET, stored in localStorage via Zustand.
 */

import React, { useState } from 'react';

declare const __APP_VERSION__: string;
import { Button, Card } from '@playground/ui';
import { useAdminStore } from '@/store/admin.js';
import { apiFetch } from '@/platform/api.js';
import { Dashboard } from './admin/Dashboard.js';
import { Games } from './admin/Games.js';
import { Settings } from './admin/Settings.js';
import { Players } from './admin/Players.js';
import { GgrStats } from './admin/GgrStats.js';
import { Frontend } from './admin/Frontend.js';
import { UserGames } from './admin/UserGames.js';

// ---------------------------------------------------------------------------
// Login screen
// ---------------------------------------------------------------------------

function AdminLogin() {
  const { setAdminToken } = useAdminStore();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setChecking(true);
    try {
      // Validate by hitting a protected endpoint
      const res = await apiFetch('/api/admin/stats', {
        headers: { Authorization: `Bearer ${password}` },
      });
      if (res.ok) {
        setAdminToken(password);
      } else {
        setError('Incorrect password. Try again.');
      }
    } catch {
      setError('Could not reach the server.');
    } finally {
      setChecking(false);
    }
  }

  return (
    <main className="flex min-h-dvh items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <Card className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <p className="text-2xl">🔐</p>
          <h1 className="mt-2 text-xl font-bold text-gray-900 dark:text-gray-100">Admin Panel</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Enter the admin password to continue.</p>
        </div>
        <form onSubmit={(e) => void handleLogin(e)} className="flex flex-col gap-3">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            autoFocus
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-500"
          />
          {error && <p className="text-xs text-red-600">{error}</p>}
          <Button variant="primary" type="submit" fullWidth loading={checking}>
            Sign In
          </Button>
        </form>
      </Card>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Admin shell
// ---------------------------------------------------------------------------

type Tab = 'dashboard' | 'games' | 'user-games' | 'frontend' | 'settings' | 'players' | 'stats';

const TABS: { id: Tab; label: string }[] = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'games', label: 'Games' },
  { id: 'user-games', label: 'User Games' },
  { id: 'frontend', label: 'Frontend' },
  { id: 'settings', label: 'Settings' },
  { id: 'players', label: 'Players' },
  { id: 'stats', label: 'GGR Stats' },
];

function AdminShell({ token }: { token: string }) {
  const { clearAdminToken } = useAdminStore();
  const [tab, setTab] = useState<Tab>('dashboard');

  return (
    <main className="mx-auto max-w-3xl px-4 py-8">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Admin Panel</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Playground — server configuration</p>
        </div>
        <Button variant="ghost" onClick={clearAdminToken}>Sign Out</Button>
      </div>

      {/* Tab bar */}
      <div className="mb-6 flex gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-700">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={[
              'flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              tab === t.id
                ? 'bg-white text-indigo-700 shadow-sm dark:bg-gray-600 dark:text-indigo-200'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200',
            ].join(' ')}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'dashboard' && <Dashboard token={token} />}
      {tab === 'games' && <Games token={token} />}
      {tab === 'user-games' && <UserGames token={token} />}
      {tab === 'frontend' && <Frontend token={token} />}
      {tab === 'settings' && <Settings token={token} />}
      {tab === 'players' && <Players token={token} />}
      {tab === 'stats' && <GgrStats token={token} />}

      {/* Version footer */}
      <p className="mt-8 text-xs text-center text-gray-400 dark:text-gray-600">
        Version: {__APP_VERSION__}
      </p>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Main Admin component — decides login vs shell
// ---------------------------------------------------------------------------

export function Admin() {
  const { adminToken } = useAdminStore();
  if (!adminToken) return <AdminLogin />;
  return <AdminShell token={adminToken} />;
}
